<script setup lang="ts">
  /**
   * Agency settings page. Provides the settings data layer + renders the
   * setting groups. Owns no per-setting logic — each group SFC renders its
   * own settings, and each setting SFC is the self-contained unit.
   *
   * The agency-scoping chrome (structure provider, picker, page header, and
   * the node-id navigation) lives in the admin `AgencySettingsShell` wrapper
   * mounted as the route-ancestor, so this view stays free of an
   * `@gat-ship/agency` dependency.
   */

  import { computed, onBeforeUnmount, onMounted } from 'vue'
  import BillingAndInvoicingGroup from '../components/BillingAndInvoicingGroup.vue'
  import { provideAgencySettings } from '../composables/useAgencySettings'
  import { useSettingDefinitions } from '../composables/useSettingDefinitions'

  const props = defineProps<{
    /** Agency node id from the route. */
    nodeId?: string
  }>()

  const agencyId = computed(() => props.nodeId)

  // Install the data layer. No pre-registered names — each per-setting
  // SFC fetches itself on mount via `useAgencySettings().refetch(...)`.
  provideAgencySettings(agencyId)

  // Kick off the definitions fetch once per session (cached).
  useSettingDefinitions().prefetch()

  onMounted(() => {
    document.title = 'Agency Settings'
  })

  onBeforeUnmount(() => {
    document.title = 'Gat Ship'
  })
</script>

<template>
  <div class="flex flex-col gap-6">
    <BillingAndInvoicingGroup />
  </div>
</template>
